import type React from "react";
import { createContext } from "react";
import useSwr from "swr";
import { CONTENT_BASE_URL } from "../lib/constants";

/**
 * サービス記事マニフェスト（apps/content の Astro がビルド時に静的生成）。
 * URL: `${CONTENT_BASE_URL}/blog/service-articles.json`
 * 形状: serviceId をキーに、publishedAt 降順の記事メタ配列。記事が無いサービスはキー自体が無い。
 */
export interface ServiceArticleEntry {
  slug: string;
  /** EN 版の記事が存在するか。false のとき EN 閲覧者には JA 記事へ誘導する（404 回避） */
  hasEn: boolean;
}

export type ServiceArticlesManifest = Record<string, ServiceArticleEntry[]>;

/** 未取得・取得失敗・Provider 未設置で共通して返す空マニフェスト（参照を固定して再レンダーを防ぐ） */
export const EMPTY_MANIFEST: ServiceArticlesManifest = {};

export const ServiceArticlesContext = createContext<ServiceArticlesManifest>(EMPTY_MANIFEST);

const MANIFEST_URL = `${CONTENT_BASE_URL}/blog/service-articles.json`;

async function fetchServiceArticlesManifest(url: string): Promise<ServiceArticlesManifest> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch service articles manifest: ${res.status}`);
  }
  return res.json();
}

/**
 * マニフェストを取得してツリー全体に配る。
 *
 * - サービスカードは仮想化リストで同時に数十枚マウントされるため、購読はここ 1 箇所に集約する
 *   （カードごとに SWR を張ると行リサイクルのたびに購読が張り直される）。
 * - ビルド時生成の静的 JSON のため immutable 扱い（再検証・リトライを抑制）。
 * - fetch 失敗時は空マニフェストとして扱い、エラーを UI に出さない（記事アイコンが単に非表示になる）。
 */
export const ServiceArticlesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data } = useSwr<ServiceArticlesManifest>(MANIFEST_URL, fetchServiceArticlesManifest, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
  });

  return (
    <ServiceArticlesContext.Provider value={data ?? EMPTY_MANIFEST}>
      {children}
    </ServiceArticlesContext.Provider>
  );
};
